import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { mangas, categories, mockReviews } from '@/mocks/data';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`text-xs ${i < Math.floor(rating) ? 'ri-star-fill text-accent-500' : i < rating ? 'ri-star-half-fill text-accent-500' : 'ri-star-fill text-background-300'}`}
        ></i>
      ))}
    </div>
  );
}

export default function HomePage() {
  const [activeTab, setActiveTab] = useState<'hot' | 'new'>('hot');

  const popularMangas = [...mangas].sort((a, b) => b.views - a.views).slice(0, 8);
  const latestMangas = [...mangas].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 8);
  const displayMangas = activeTab === 'hot' ? popularMangas : latestMangas;

  return (
    <>
      <ScrollToTop />
      <Header />

      <main>
        <section className="relative h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20illustration%20of%20an%20antique%20bookstore%20interior%20with%20wooden%20bookshelves%20filled%20with%20colorful%20manga%20volumes%2C%20soft%20golden%20light%20streaming%20through%20a%20large%20window%2C%20floating%20dust%20particles%20in%20the%20light%2C%20cozy%20reading%20nook%20with%20a%20vintage%20armchair%2C%20artistic%20brushstroke%20style%2C%20warm%20orange%20and%20cream%20tones%2C%20whimsical%20atmosphere%2C%20storybook%20aesthetic&width=1600&height=900&seq=hero-manga-bg&orientation=landscape"
            alt="洞洞杂货铺漫画 - 在线免费阅读漫画"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>

          <div className="relative z-10 w-full text-center px-4">
            <div className="max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
                <i className="ri-sparkling-line text-accent-400 text-sm"></i>
                <span className="text-sm text-white/90">无需付费等待，打开即可沉浸式追漫</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                洞洞杂货铺漫画
              </h1>
              <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
                在线免费阅读洞洞杂货铺漫画全集尽在178漫画网，涵盖热血、恋爱、奇幻、悬疑等多种题材漫画
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/manga/dongdong-zahuopu"
                  className="px-8 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  开始阅读
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <Link
                  to="/category/all"
                  className="px-8 py-3 rounded-full bg-white/10 backdrop-blur-sm text-white border border-white/30 font-semibold text-sm hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
                >
                  浏览全部漫画
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-background-100 hover:bg-primary-100 text-foreground-700 hover:text-primary-700 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900">热门推荐</h2>
              <div className="flex items-center gap-1 bg-background-200/60 rounded-full p-1">
                <button
                  onClick={() => setActiveTab('hot')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === 'hot' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  人气排行
                </button>
                <button
                  onClick={() => setActiveTab('new')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === 'new' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  最近更新
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {displayMangas.map((manga, index) => (
                <Link
                  key={manga.id}
                  to={`/manga/${manga.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                    <img
                      src={manga.cover}
                      alt={`${manga.title}漫画封面`}
                      title={`${manga.title} - 在线免费阅读`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${manga.status === '已完结' ? 'bg-accent-500' : 'bg-primary-500'}`}>
                        {manga.status}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <p className="text-white text-xs truncate">
                        {manga.rating.toFixed(1)} 分 · {manga.views > 10000 ? `${(manga.views / 10000).toFixed(0)}万` : manga.views} 阅读
                      </p>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                    {manga.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1 truncate">{manga.author} · {manga.tags.slice(0, 2).join('/')}</p>
                </Link>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-1 px-6 py-2.5 rounded-full border border-background-300 text-foreground-600 hover:text-foreground-900 hover:border-foreground-300 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部漫画
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-1">最新更新</h2>
                <p className="text-sm text-foreground-500">每日更新，精彩不停</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {mangas.slice(0, 6).map((manga) => (
                <Link
                  key={manga.id}
                  to={`/manga/${manga.id}`}
                  className="flex gap-4 p-4 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer group"
                >
                  <div className="w-20 h-28 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
                    <img src={manga.cover} alt={manga.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex flex-col justify-between min-w-0">
                    <div>
                      <h3 className="font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                        {manga.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{manga.author}</p>
                      <p className="text-xs text-foreground-600 mt-1 line-clamp-2 leading-relaxed">{manga.description}</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-foreground-500 mt-2">
                      <span className="text-accent-600 font-medium">{manga.rating.toFixed(1)}分</span>
                      <span>更新至{manga.chapters.length}话</span>
                      <span className="text-foreground-400">{manga.updatedAt}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-screen-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-8">读者评价</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {mockReviews.slice(0, 8).map((review, index) => (
                <div key={index} className="p-5 rounded-lg bg-background-50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-700">{review.author[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.author}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`text-[10px] ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-background-300'}`}
                          ></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                  <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-2xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-4">加入我们的追漫社区</h2>
            <p className="text-foreground-600 mb-8 max-w-lg mx-auto leading-relaxed">
              洞洞杂货铺漫画承诺永久免费，无需充值会员，无需等待解锁，打开即可畅读所有漫画章节。
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line"></i>
              立即免费阅读
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: '洞洞杂货铺漫画',
          alternateName: '178漫画网',
          url: 'https://www.178mh.com',
          description: '在线免费阅读漫画全集平台',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://www.178mh.com/search?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '洞洞杂货铺漫画 - 首页',
          description: '在线免费阅读洞洞杂货铺漫画全集尽在178漫画网，无需付费等待，打开即可沉浸式追漫。',
          url: 'https://www.178mh.com',
          isPartOf: {
            '@type': 'WebSite',
            name: '洞洞杂货铺漫画',
          },
        })}
      </script>
    </>
  );
}